import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { EmptyState, ListTable, Panel, StatusChip, type ListColumn, type StatusTone } from '../../../ui-kit';
import { fetchPsIntegrations, type PsIntegration } from '../../../services/policyServicingService';
import { DIALER_PARTS, KlarisMark } from '../rocco/IntegrationPicker';
import { fromNow, useLive } from '../rocco/live';
import { MailboxLogo, PolicySystemLogo, WarehouseLogo } from './logos';
import { useAgent } from '../AgentContext';

const whole = (v: number) => v.toLocaleString();

function stateOf(row: PsIntegration): { tone: StatusTone; label: string } {
  if (!row.connected) return { tone: 'neutral', label: 'Not connected' };
  if (row.error) return { tone: 'warning', label: 'Needs attention' };
  return { tone: 'good', label: 'Connected' };
}

function logoOf(row: PsIntegration): React.ReactNode {
  if (row.id === 'mailbox') return <MailboxLogo provider={row.name.toLowerCase()} />;
  if (row.id === 'policy_system') return <PolicySystemLogo />;
  if (row.id === 'warehouse') return <WarehouseLogo name={row.name} />;
  if (row.id === 'carrier_email') return <MailboxLogo provider="outlook" />;
  return <KlarisMark size={22} />;
}

function activityOf(row: PsIntegration): string {
  if (!row.connected) {
    if (row.id === 'mailbox') return 'Connect the service inbox to start';
    if (row.id === 'carrier_email') return 'Carriers are written to from the service inbox, once it is connected';
    return 'Not connected';
  }
  if (row.id === 'mailbox') {
    return row.checked_at
      ? `${row.address} · checked ${fromNow(row.checked_at)} · ${whole(row.handled_today ?? 0)} emails today`
      : `${row.address} · not checked yet`;
  }
  if (row.id === 'warehouse' || row.id === 'policy_system' || row.id === 'carrier_email') return row.detail || '';
  return `${whole(row.calls_today ?? 0)} carrier calls today`;
}

/** Other systems that take the same place as the ones in use. */
const ALSO_WORKS_WITH: Record<PsIntegration['id'], string> = {
  mailbox: 'Outlook or Gmail',
  warehouse: 'Redshift, Snowflake, BigQuery or Postgres',
  policy_system: 'Boston Software, AMS360, Applied Epic or HawkSoft, once connected',
  carrier_email: 'a phone call by the AI voice agent, when the agent is set to call',
  dialer: 'Regal, Aircall, Genesys or Dialpad',
};

/**
 * What the Policy Servicing agent is joined to, as it is right now: the
 * service inbox the requests arrive in, the warehouse the policies are read
 * from, and the dialer that calls the carriers.
 */
export default function IntegrationsPage() {
  const { agent } = useAgent();
  const fetcher = React.useCallback(() => fetchPsIntegrations(agent.id), [agent.id]);
  const { data, error, loading } = useLive(fetcher, 10_000);
  if (error && !data) return <EmptyState message={error} />;

  const columns: ListColumn<PsIntegration>[] = [
    {
      key: 'name',
      header: 'System',
      grow: 1.2,
      minWidth: 200,
      render: (row) => (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>{logoOf(row)}</Box>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{row.name}</Typography>
            {row.built_on ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {row.built_on.map((part, i) => (
                  <React.Fragment key={part}>
                    {i > 0 ? <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>+</Typography> : null}
                    <Box sx={{ display: 'flex' }}>{DIALER_PARTS[part]}</Box>
                    <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{part}</Typography>
                  </React.Fragment>
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      ),
    },
    {
      key: 'role',
      header: 'Does',
      width: 130,
      render: (row) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{row.role}</Typography>,
    },
    {
      key: 'state',
      header: 'State',
      width: 150,
      render: (row) => <StatusChip {...stateOf(row)} />,
    },
    {
      key: 'activity',
      header: 'Today',
      grow: 2,
      minWidth: 220,
      render: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{activityOf(row)}</Typography>
          {row.error ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{row.error}</Typography>
          ) : (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              Also works with {ALSO_WORKS_WITH[row.id]}
            </Typography>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Panel title="Connected systems" subtitle="Requests in, policies read, carriers asked · as they are now">
      <ListTable
        columns={columns}
        rows={data?.integrations ?? []}
        getRowKey={(row) => row.id}
        loading={loading}
        emptyMessage="Nothing connected."
      />
    </Panel>
  );
}
